<template>
    <tr :class="`${isDeleteRequested || isNonAdmitRequested || isTransferRequested ? 'edit-row' : ''}`">
        <td>
            <span v-if="referral.referralDate">{{ formatDateFromDate(referral.referralDate) }}</span>
            <small v-else><i class="text-default">Not Available</i></small>
        </td>
        <td>
            <span v-if="referral.requestedStartOfCareDate">{{
                formatDateFromDate(referral.requestedStartOfCareDate)
            }}</span>
            <small v-else><i class="text-default">Not Available</i></small>
        </td>
        <td>
            <FormatName :first-name="referral.firstName" :last-name="referral.lastName" />
            <br />
            <i class="me-2 fas fa-mobile-alt" v-if="referral.phoneNumbers && referral.phoneNumbers?.length > 0"></i>
            <a :href="`tel:${getPhoneNumber(referral.phoneNumbers)}`">{{
                formatPhone(getPhoneNumber(referral.phoneNumbers))
            }}</a>
        </td>
        <td>
            <EmptyTextHandler :text="referral.branchName" />
        </td>
        <td v-if="referral.lineOfService != null">{{ lineOfServiceEnumMap.getEnumName(referral.lineOfService) }}</td>
        <td v-else><EmptyTextHandler :text="referralApplication" /></td>
        <td>
            <EmptyTextHandler :text="referral.referralSource" />
        </td>
        <td>
            <EmptyTextHandler :text="formatDateFromDate(referral.dateOfBirth)" />
        </td>
        <td>
            <EmptyTextHandler :text="getGenderTypes(referral.gender)" />
        </td>
        <td>
            <EmptyTextHandler :text="referral.createdBy" />
        </td>
        <td>
            <EmptyTextHandler :text="referral.liaison" />
        </td>    
          <td>
            <EmptyTextHandler :text="referral.tags" />
        </td>
        <td v-if="!isDeleteRequested && !isNonAdmitRequested" class="text-end">
         <div class="btn table-btn">
                 <div v-if="referral.comments?.length > 0" v-tooltip="{ title: referral.comments }">
                <i class="far fa-comment-dots icon-size text-info"></i>
            </div>
            <span v-else class="icon-cur">
                <i class="far fa-comment-dots icon-size"></i>
            </span>
                </div>
            <template v-if="isEditAllowed || isDeleteAllowed">
                <button
                    type="button"
                    class="btn table-btn px-2 btn-outline-info au-target"
                    @click="editReferralForSelectedPatient()"
                    v-if="isEditAllowed"
                    v-tooltip="{ title: 'Edit Referral', placement: 'bottom' }"
                >
                    <i class="fas fa-edit icon-size"></i>
                </button>
                <button
                    type="button"
                    class="btn table-btn btn-outline-info"
                    tooltip="title: Refer Patient to Another Provider; placement: bottom"
                    @click="openTransferForm()"
                    v-tooltip="{ title: 'Refer Patient to Another Provider', placement: 'bottom' }"
                >
                    <i class="fas fa-exchange-alt"></i>
                </button>
               
                <button
                    type="button"
                    :class="{
                        'btn-outline-info color-info': referral?.hasPreadmissionNote,
                        'color-default': !referral?.hasPreadmissionNote
                    }"
                    class="btn table-btn px-1 au-target"
                    @click="openPreadmissionModal()"
                    v-tooltip="{ title: 'Preadmission Notes', placement: 'bottom' }"
                    v-if="isHomeHealthAgency || isHospiceAgency || isPalliativeAgency"
                >
                    <i class="fas fa-sticky-note icon-size"></i>
                </button>
                <button
                    type="button"
                    class="btn table-btn px-2 btn-outline-info au-target"
                    @click="openAddDocumentModal(referral.application)"
                    v-tooltip="{ title: 'Attach Document', placement: 'bottom' }"
                >
                    <i class="fas fa-paperclip icon-size"></i>
                </button>
                <button
                    type="button"
                    class="btn px-1 py-0 ms-1 btn-outline-primary icon-size"
                    data-bs-toggle="dropdown"
                    id="ReferralRow__Actions${id}"
                >
                    <i class="fas fa-ellipsis-h icon-size"></i>
                </button>
                <div class="dropdown-menu" aria-labelledby="ReferralRow__Actions${id}">
                    <a
                        class="dropdown-item"
                        @click="openNonAdmitForm(true)"
                        v-if="isEditAllowed"
                        href="javascript:void(0);"
                    >
                        Non-Admit
                    </a>
                    <a
                        class="dropdown-item"
                        v-if="isConvertToPendingAllowed"
                        @click="handleConvertToPending(referral)"
                        href="javascript:void(0);"
                    >
                        Convert to Pending
                    </a>
                    <a
                        class="dropdown-item near-ai-button"
                        v-if="canShowPremium"
                        href="javascript:void(0);"
                        @click="handleFindNearByEmployees(referral)"
                    >
                        Find Nearby Employees
                    </a>
                    <a
                        class="dropdown-item"
                        href="javascript:void(0);"
                        @click="confirmDeleteReferral()"
                        v-if="isDeleteAllowed"
                    >
                        Delete
                    </a>
                </div>
                <!--  -->
            </template>
            <i v-else> No Actions </i>
        </td>
        <!-- Delete -->
        <td colspan="4" v-if="isDeleteRequested">
            <TablePrompt
                ok-text="Delete"
                cancelText="Cancel"
                name="Referral"
                message="Do you want to delete this referral?"
                :includeYes="false"
                :isInProgress="isDeleteInProgress"
                :ok="deleteReferral"
                :cancel="cancelDeleteReferral"
            />
        </td>
        <td colspan="2" class="text-end" v-if="isNonAdmitRequested">
            <div class="big-text">
                <i>Non-Admit in Progress</i>
            </div>
        </td>
    </tr>

    <tr class="non-admit-form-row-two" v-if="isNonAdmitRequested">
        <td :colspan="columns" class="p-3 edit au-target">
            <div au-target-id="97">
                <div class="card-body p-0">
                    <section class="p-2 pb-0 d-flex justify-content-center au-target" au-target-id="2034">
                        <div class="p-2 form-group">
                            <label for="non-admit-date">
                                Non-Admit Date
                                <span class="text-danger">*</span>
                            </label>
                            <DatePicker :getDate="getNewNonAdmitDate">
                                <div class="input-group date datepickerInput">
                                    <input
                                        type="text"
                                        class="form-control"
                                        placeholder="MM/DD/YYYY"
                                        v-model="newNonAdmitData.nonAdmitDate"
                                        autocomplete="off"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                            </DatePicker>
                        </div>
                        <div class="p-2 form-group mw-60">
                            <label for="non-admit-reason">
                                Non-Admit Reason
                                <span class="text-danger">*</span>
                            </label>
                            <div v-if="!showMultiSelect">
                                <NewTypeahead
                                    :data="nonAdmitReasons"
                                    v-model:selectedOption="selectedNonAdmitReason"
                                    :clearOnBlur="true"
                                    placeholder="Enter Non-Admit Reason"
                                />
                            </div>
                            <div v-else class="new-typeahead">
                                <div class="multi-select-filter">
                                    <TagsInput
                                        :data="nonAdmitReasons"
                                        :tags="mulitpleNonAdmitReasons"
                                        placeholder="Select Non-Admit Reason"
                                        v-on:updateSelectedOption="updateSelectedNonAdmitReason"
                                        v-on:updateTags="updateTags"
                                        :open-on-focus="true"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                    <section class="d-flex justify-content-center">
                        <div class="col-6 pb-4 form-group" v-if="showCommentBox">
                            <label for="non-admit-comments">
                                Comments
                                <span v-if="showMultiSelect && commentRequired" class="text-danger">*</span>
                            </label>
                            <CommentInput
                                input-name="nonadmit - comments"
                                :result="newNonAdmitData.nonAdmitComments"
                                v-on:resultUpdatedAppended="resultUpdatedAppended"
                                v-on:resultUpdated="resultUpdated"
                                :show-template-typeahead="showTemplate"
                                :comment-required="commentRequired"
                            />
                        </div>
                    </section>
                </div>
            </div>
            <div class="text-center">
                <button
                    type="button"
                    class="btn btn-primary btn-md au-target"
                    @click="convertToNonAdmit()"
                    au-target-id="2039"
                >
                    Non-Admit
                </button>
                <button
                    type="button"
                    class="btn btn-outline-secondary btn-md ms-2 au-target"
                    @click="closeNonAdmitForm()"
                    au-target-id="2040"
                >
                    Cancel
                </button>
            </div>
        </td>
    </tr>
    <tr v-if="isTransferRequested" class="edit-row">
        <td :colspan="columns" class="p-3 edit">
            <TransferPatientForm
                :referral="referral"
                v-model:isTransferRequested="isTransferRequested"
            ></TransferPatientForm>
        </td>
    </tr>
</template>

<script lang="ts" src="./CentralReferralRow.ts"></script>
<style src="./CentralReferralRow.scss"></style>
